import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

// Components
import FollowUs from '../FollowUs'
import DownloadApps from '../DownloadApps'

// Libs
import { handleGtmClickAds, handleGtmClickCustomService, handleGtmClickNavigation } from '@/methods/handleGtmEvent'
import useI18n from '@/hooks/useI18n'
import { useSiteNav } from '@/hooks/useSiteNav'
import { fireSiteNavGtm, pickSiteNavLabel, resolveSiteNavUrl } from '@/methods/siteNav'

// Constants
import { ROUTE_PATH } from '@/constants/routePath'
import { EXTERNAL_LINK } from '@/constants/externalLink'
import { GTM_LOCATION, GtmNavigationParams, GtmClickAdsClickText } from '@/constants/gtmEvent'
import { IServiceCategory } from '../..'
import { REGION } from '@/constants/region'

export const useServiceList = () => {
  const { t } = useTranslation()
  const { locale } = useRouter()
  const { isOverseaUser } = useI18n()
  const { data: siteNav } = useSiteNav()

  // 「追蹤我們」「下載 App」不是連結，不在後台「網站選單」裡，固定接在最後
  const fixedServices: IServiceCategory[] = [
    {
      title: t('Common_Follow_Us'),
      items: [
        {
          children: <FollowUs />,
        },
      ],
      isMobileFullWidth: true,
    },
    {
      title: t('Footer_Download_App'),
      items: [
        {
          children: <DownloadApps />,
        },
      ],
      isMobileFullWidth: true,
    },
  ]

  if (siteNav) {
    const navServices: IServiceCategory[] = siteNav.footer.map((column) => ({
      title: pickSiteNavLabel(column, locale),
      regions: column.regions,
      items: column.links.map((link) => ({
        children: pickSiteNavLabel(link, locale),
        url: resolveSiteNavUrl(link.url, locale),
        openNewTab: link.openNewTab,
        regions: link.regions,
        onClick: () => fireSiteNavGtm(link.gtmEvent, t),
      })),
    }))

    return [...navServices, ...fixedServices]
  }

  // 後台「網站選單」沒回來（載入中 / 失敗）時用的預設頁尾
  const services: IServiceCategory[] = [
    {
      title: t('Footer_Explore'),
      items: [
        {
          children: t('Header_Live'),
          url: ROUTE_PATH.LIVE,
        },
        {
          children: t('Header_Mall'),
          url: ROUTE_PATH.MALL,
        },
      ],
    },
    {
      title: t('Footer_Help_Center'),
      items: [
        {
          children: t('Header_Secondary_Menu_FAQ'),
          allowRegions: new Set([REGION.TW]),
          url: EXTERNAL_LINK.FAQ,
          onClick: () => {
            handleGtmClickNavigation({ clickText: t(GtmNavigationParams.FAQ, { lng: 'zh-tw' }) as GtmNavigationParams })
          },
        },
        {
          children: t('Header_Secondary_Menu_Customer_Service_Center'),
          allowRegions: new Set([REGION.TW, REGION.HK, REGION.KR]),
          url: isOverseaUser ? EXTERNAL_LINK.CUSTOMER_SERVICE_CENTER_HK : EXTERNAL_LINK.CUSTOMER_SERVICE_CENTER,
          onClick: () => {
            handleGtmClickCustomService({ location: GTM_LOCATION.NAVIGATION })
          },
        },
        {
          children: t('Header_Secondary_Menu_Return_Policy'),
          allowRegions: new Set([REGION.TW]),
          url: EXTERNAL_LINK.RETURN_POLICY,
          onClick: () => {
            handleGtmClickNavigation({ clickText: t(GtmNavigationParams.RETURN_POLICY, { lng: 'zh-tw' }) as GtmNavigationParams })
          },
        },
      ],
    },
    {
      title: t('Footer_About_Platform'),
      items: [
        {
          children: t('Footer_Legal_Statement'),
          allowRegions: new Set([REGION.TW, REGION.HK, REGION.KR]),
          url: isOverseaUser ? EXTERNAL_LINK.LEGAL_DECLAIMER_EN : EXTERNAL_LINK.LEGAL_DECLAIMER,
          onClick: () => {
            handleGtmClickNavigation({ clickText: t(GtmNavigationParams.TERMS_OF_SERVICE, { lng: 'zh-tw' }) as GtmNavigationParams })
          },
        },
        {
          children: t('Footer_Privacy_Policy'),
          allowRegions: new Set([REGION.TW, REGION.HK, REGION.KR]),
          url: isOverseaUser ? EXTERNAL_LINK.PRIVACY_POLICY_EN : EXTERNAL_LINK.PRIVACY_POLICY,
          onClick: () => {
            handleGtmClickNavigation({ clickText: t(GtmNavigationParams.TERMS_OF_SERVICE, { lng: 'zh-tw' }) as GtmNavigationParams })
          },
        },
        {
          children: t('Footer_Terms_Of_Service'),
          allowRegions: new Set([REGION.TW, REGION.HK, REGION.KR]),
          url: isOverseaUser ? EXTERNAL_LINK.USER_TERMS_EN : EXTERNAL_LINK.USER_TERMS,
          onClick: () => {
            handleGtmClickNavigation({ clickText: t(GtmNavigationParams.TERMS_OF_SERVICE, { lng: 'zh-tw' }) as GtmNavigationParams })
          },
        },
      ],
    },
    {
      title: t('Footer_Join_Us'),
      items: [
        {
          children: t('Header_Secondary_Menu_Become_Merchant'),
          allowRegions: new Set([REGION.TW]),
          url: EXTERNAL_LINK.BECOME_MERCHANT,
          onClick: () => {
            handleGtmClickAds({ clickText: t(GtmClickAdsClickText.RECRUITMENT_MERCHANT, { lng: 'zh-tw' }) })
          },
        },
        {
          children: t('Header_Secondary_Menu_Become_KOL'),
          allowRegions: new Set([REGION.TW]),
          url: EXTERNAL_LINK.BECOME_KOL,
          onClick: () => {
            handleGtmClickAds({ clickText: t(GtmClickAdsClickText.RECRUITMENT_KOL, { lng: 'zh-tw' }) })
          },
        },
      ],
    },
    ...fixedServices,
  ]

  return services
}
